import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X, Volume2, VolumeX } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { useTranslation } from "react-i18next";
import { indexStore } from "../../store/indexStore";
import UAEDirhamIcon from "../utils/UAEDirhamIcon";

const AudioNotification = () => {
  const { t } = useTranslation();
  const {
    newOrderNotification,
    isNotificationVisible,
    closeNotification,
    unreadOrdersCount,
  } = indexStore();

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const [showNotification, setShowNotification] = useState(true);

  const playNotificationSound = useCallback(() => {
    try {
      // إيقاف المشغل السابق إذا كان موجوداً
      if (audioRef.current) {
        stopSound();
      }

      // إنشاء صوت إشعار باستخدام Web Audio API
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      // إنشاء نغمة إشعار لطيفة ومريحة
      const createTone = (frequency, startTime, duration, volume) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // إعداد النغمة - استخدام sine wave بدلاً من square
        oscillator.frequency.setValueAtTime(frequency, startTime);
        oscillator.type = "sine"; // صوت أكثر لطفاً

        // إعداد الصوت التصاعدي اللطيف مع وضوح أكبر
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume * 0.4, startTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(volume * 0.6, startTime + 0.1);
        gainNode.gain.linearRampToValueAtTime(volume * 0.8, startTime + 0.15);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.2);
        gainNode.gain.linearRampToValueAtTime(
          volume * 0.8,
          startTime + duration - 0.15
        );
        gainNode.gain.linearRampToValueAtTime(
          volume * 0.4,
          startTime + duration - 0.05
        );
        gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      // تشغيل نغمات لطيفة ومريحة مع صوت تدريجي أعلى
      const playSequence = () => {
        const currentTime = audioContext.currentTime;

        // التسلسل الأول - صوت متوسط
        createTone(523, currentTime, 0.3, 0.6); // C5 - صوت متوسط
        createTone(659, currentTime + 0.1, 0.3, 0.6); // E5 - صوت متوسط
        createTone(784, currentTime + 0.2, 0.4, 0.6); // G5 - صوت متوسط

        // التسلسل الثاني - صوت عالي (بعد 0.5 ثانية)
        setTimeout(() => {
          const currentTime2 = audioContext.currentTime;
          createTone(523, currentTime2, 0.3, 0.8); // C5 - صوت عالي
          createTone(659, currentTime2 + 0.1, 0.3, 0.8); // E5 - صوت عالي
          createTone(784, currentTime2 + 0.2, 0.4, 0.8); // G5 - صوت عالي
        }, 500);

        // التسلسل الثالث - صوت عالي جداً (بعد 1 ثانية)
        setTimeout(() => {
          const currentTime3 = audioContext.currentTime;
          createTone(523, currentTime3, 0.3, 1.0); // C5 - صوت عالي جداً
          createTone(659, currentTime3 + 0.1, 0.3, 1.0); // E5 - صوت عالي جداً
          createTone(784, currentTime3 + 0.2, 0.4, 1.0); // G5 - صوت عالي جداً
        }, 1000);
      };

      // تشغيل التسلسل الأول
      playSequence();

      // تكرار التسلسل كل 3 ثوان (يستمر حتى إغلاق الإشعار)
      const intervalId = setInterval(() => {
        if (!isMuted && showNotification && newOrderNotification) {
          playSequence();
        }
      }, 3000);

      // حفظ interval ID للتنظيف لاحقاً
      audioRef.current = { intervalId, audioContext };
      setIsPlaying(true);
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  }, [isMuted, showNotification, newOrderNotification]);

  // إنشاء صوت إشعار باستخدام Web Audio API
  useEffect(() => {
    console.log("AudioNotification - Order received:", newOrderNotification);
    if (newOrderNotification && !isMuted && isNotificationVisible) {
      playNotificationSound();
    } else if (!newOrderNotification || !isNotificationVisible) {
      // إيقاف الصوت إذا لم يكن هناك طلب جديد أو تم إغلاق الإشعار
      stopSound();
    }
  }, [
    newOrderNotification,
    isMuted,
    isNotificationVisible,
    playNotificationSound,
  ]);

  // تنظيف الصوت عند إلغاء المكون
  useEffect(() => {
    return () => {
      stopSound();
    };
  }, []);

  const stopSound = () => {
    if (audioRef.current) {
      // إيقاف التكرار
      if (audioRef.current.intervalId) {
        clearInterval(audioRef.current.intervalId);
      }
      // إغلاق audio context
      if (audioRef.current.audioContext) {
        audioRef.current.audioContext.close();
      }
      audioRef.current = null;
    }
    setIsPlaying(false);
  };

  const handleClose = () => {
    stopSound();
    setShowNotification(false);
    closeNotification();
  };

  const toggleMute = () => {
    if (isMuted) {
      // إلغاء الكتم - تشغيل الصوت مرة أخرى
      setIsMuted(false);
      if (newOrderNotification) {
        playNotificationSound();
      }
    } else {
      // كتم الصوت
      stopSound();
      setIsMuted(true);
    }
  };

  if (!showNotification || !newOrderNotification || !isNotificationVisible) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
      <Card className="w-80 shadow-lg border-l-4 border-l-green-500 bg-white">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Bell className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {t("gloabl.new_order")}
                </h3>
                <p className="text-sm text-gray-600">
                  {t("gloabl.order")} #{newOrderNotification.order_number}
                </p>
                <p className="text-xs text-gray-500">
                  {newOrderNotification.type.replace("_", " ")} -{" "}
                  <UAEDirhamIcon className="w-3 h-3 inline" />
                  {newOrderNotification.total}
                </p>
                {unreadOrdersCount > 0 && (
                  <p className="text-xs text-red-600 font-bold">
                    {unreadOrdersCount} {t("gloabl.unread_orders")}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMute}
                className="h-8 w-8 p-0"
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {isPlaying && !isMuted && (
            <div className="mt-3 flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-1 h-4 bg-red-500 animate-pulse"></div>
                <div
                  className="w-1 h-4 bg-orange-500 animate-pulse"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-1 h-4 bg-yellow-500 animate-pulse"
                  style={{ animationDelay: "0.2s" }}
                ></div>
                <div
                  className="w-1 h-4 bg-green-500 animate-pulse"
                  style={{ animationDelay: "0.3s" }}
                ></div>
                <div
                  className="w-1 h-4 bg-blue-500 animate-pulse"
                  style={{ animationDelay: "0.4s" }}
                ></div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AudioNotification;
